import { c as _c } from "react/compiler-runtime";
import { FaroTextButton } from "@components/common/faro-text-button";
import { PageContainer } from "@components/common/page-container";
import { ThumbnailImage } from "@components/common/thumbnail-image";
import { ExternalLinks } from "@components/external-links";
import { Divider, ShareLinkIcon, ViewerIcon, ProjectsDuoIcon, Text } from "@flat-ui";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { CreateProjectDialog } from "@pages/projects/create-project-dialog";
import { ProjectShareDialog } from "@pages/projects/project-share-dialog";
import { ProjectTabs } from "@router/route-params";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { DemoTag } from "@src/components/common/demo-tag";
import { EmptyPage } from "@src/components/common/empty-page/empty-page";
import { userMenuLink, withEllipsisTwoLines } from "@styles/common-styles";
import { defaultProjectThumbnail, getTrackProjectDateProps, openProjectInViewer } from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
/** Show this specific empty page when workspace has only the demo project (as active) */
export function ProjectsOverviewDemoEmptyPage(t0) {
  const $ = _c(75);
  const {
    demoProject,
    companyId
  } = t0;
  const {
    navigateToProjectDetail
  } = useAppNavigation();
  const {
    trackEvent,
    trackAsyncEvent
  } = useTrackEvent();
  const {
    isScreenMdOrLarger
  } = useMediaQueryList();
  const {
    canCreateProjects
  } = useHasUserValidFeatureCompanyLevel();
  const [isShareProjectDialogOpen, setIsShareProjectDialogOpen] = useState(false);
  let t1;
  if ($[0] !== companyId || $[1] !== demoProject.id) {
    t1 = generateProjectDetailsRoute(companyId, demoProject.id, ProjectTabs.overview);
    $[0] = companyId;
    $[1] = demoProject.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...userMenuLink,
      color: "primary.main"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== demoProject || $[5] !== trackEvent) {
    t3 = () => {
      trackEvent({
        name: ProjectEvents.openProjectDetails,
        props: {
          origin: "Empty projects page",
          type: "demo",
          projectId: demoProject.id,
          ...getTrackProjectDateProps(demoProject)
        }
      });
    };
    $[4] = demoProject;
    $[5] = trackEvent;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <Box component={Link} to={t1} sx={t2} onClick={t3}>demo project</Box>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const demoProjectLink = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      color: "text.secondary"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text variant="bodyS" sx={t5}>Create, store and manage projects in your workspace. Projects can be organized into groups. <ExternalLinks link="createNewProjectHelp" /></Text>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      backgroundColor: "surfaces.tertiary",
      "& .actionButtons": {
        visibility: "visible"
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      width: "100%",
      mt: "15px",
      py: "10px",
      px: "20px",
      backgroundColor: "surfaces.secondary",
      "&:hover": t7,
      cursor: "pointer",
      justifyContent: "space-between",
      alignItems: {
        xs: "flex-start",
        md: "center"
      }
    };
    t9 = {
      xs: "column",
      md: "row"
    };
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== companyId || $[16] !== demoProject.id || $[17] !== navigateToProjectDetail) {
    t10 = () => navigateToProjectDetail({
      companyId,
      projectId: demoProject.id
    });
    $[15] = companyId;
    $[16] = demoProject.id;
    $[17] = navigateToProjectDetail;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      maxHeight: "50px",
      maxWidth: "70px",
      objectFit: "contain",
      mx: "20px"
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== demoProject.thumbnailUrl) {
    t12 = <ThumbnailImage src={demoProject.thumbnailUrl} fallbackSrc={defaultProjectThumbnail} alt="Project thumbnail" sx={t11} />;
    $[20] = demoProject.thumbnailUrl;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Text variant="heading12" component="div" sx={{
      color: "text.primary",
      mb: "7px"
    }}>Demo</Text>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Box component="div">{t13}<Text variant="bodyS" sx={{
        color: "text.secondary",
        ...withEllipsisTwoLines
      }}>Customise and explore our demo project</Text></Box>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12) {
    t15 = <Stack direction="row">{t12}{t14}</Stack>;
    $[24] = t12;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = !isScreenMdOrLarger ? "100%" : undefined;
  const t17 = !isScreenMdOrLarger ? "flex" : undefined;
  const t18 = !isScreenMdOrLarger ? "space-between" : undefined;
  const t19 = !isScreenMdOrLarger ? "5px" : undefined;
  let t20;
  if ($[26] !== t16 || $[27] !== t17 || $[28] !== t18 || $[29] !== t19) {
    t20 = {
      width: t16,
      display: t17,
      justifyContent: t18,
      marginTop: t19,
      alignItems: "center"
    };
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const t21 = isScreenMdOrLarger ? "hidden" : "visible";
  let t22;
  if ($[31] !== t21) {
    t22 = {
      visibility: t21,
      alignItems: "center"
    };
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ViewerIcon style={{
      width: "20px",
      height: "20px"
    }} />;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== demoProject || $[35] !== trackAsyncEvent) {
    t24 = event => {
      event.preventDefault();
      event.stopPropagation();
      const numberOfMembers = demoProject.members.length || undefined;
      openProjectInViewer({
        project: demoProject,
        openFrom: "demo-project-empty-page",
        numberOfMembers,
        trackAsyncEvent
      });
    };
    $[34] = demoProject;
    $[35] = trackAsyncEvent;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      color: "text.primary"
    };
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const t26 = isScreenMdOrLarger ? "Open Project" : "";
  let t27;
  if ($[38] !== t24 || $[39] !== t26) {
    t27 = <FaroTextButton startIcon={t23} onClick={t24} sx={t25}>{t26}</FaroTextButton>;
    $[38] = t24;
    $[39] = t26;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Divider orientation="vertical" sx={{
      height: "20px",
      marginX: "16px"
    }} />;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsShareProjectDialogOpen(false);
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  let t31;
  let t32;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ShareLinkIcon style={{
      width: "20px",
      height: "20px"
    }} />;
    t31 = () => setIsShareProjectDialogOpen(true);
    t32 = {
      color: "text.primary",
      mr: "45px"
    };
    $[43] = t30;
    $[44] = t31;
    $[45] = t32;
  } else {
    t30 = $[43];
    t31 = $[44];
    t32 = $[45];
  }
  const t33 = isScreenMdOrLarger ? "Share Project" : "";
  let t34;
  if ($[46] !== t33) {
    t34 = <FaroTextButton startIcon={t30} onClick={t31} sx={t32}>{t33}</FaroTextButton>;
    $[46] = t33;
    $[47] = t34;
  } else {
    t34 = $[47];
  }
  let t35;
  if ($[48] !== demoProject || $[49] !== isShareProjectDialogOpen || $[50] !== t34) {
    t35 = <Box component="div" onClick={_temp}><ProjectShareDialog isOpen={isShareProjectDialogOpen} onClose={t29} project={demoProject}>{t34}</ProjectShareDialog></Box>;
    $[48] = demoProject;
    $[49] = isShareProjectDialogOpen;
    $[50] = t34;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] !== t22 || $[53] !== t27 || $[54] !== t35) {
    t36 = <Stack className="actionButtons" direction="row" sx={t22}>{t27}{t28}{t35}</Stack>;
    $[52] = t22;
    $[53] = t27;
    $[54] = t35;
    $[55] = t36;
  } else {
    t36 = $[55];
  }
  const t37 = isScreenMdOrLarger ? "20px" : "0px";
  let t38;
  if ($[56] !== t37) {
    t38 = <DemoTag sx={{
      mr: t37
    }} />;
    $[56] = t37;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] !== t20 || $[59] !== t36 || $[60] !== t38) {
    t39 = <Stack direction="row" sx={t20}>{t36}{t38}</Stack>;
    $[58] = t20;
    $[59] = t36;
    $[60] = t38;
    $[61] = t39;
  } else {
    t39 = $[61];
  }
  let t40;
  if ($[62] !== t10 || $[63] !== t15 || $[64] !== t39) {
    t40 = <Stack sx={t8} direction={t9} onClick={t10}>{t15}{t39}</Stack>;
    $[62] = t10;
    $[63] = t15;
    $[64] = t39;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  let t41;
  if ($[66] !== canCreateProjects || $[67] !== demoProjectLink) {
    t41 = canCreateProjects ? <>You haven't added projects to your workspace. Select the "new project" button to get started.</> : <>You currently don't have any projects. To get started, explore and try out our {demoProjectLink}.</>;
    $[66] = canCreateProjects;
    $[67] = demoProjectLink;
    $[68] = t41;
  } else {
    t41 = $[68];
  }
  let t42;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <CreateProjectDialog isDemo={true} />;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== t41) {
    t43 = <EmptyPage title="You don't have any project yet" subtitle={t41} icon={ProjectsDuoIcon} button={t42} />;
    $[70] = t41;
    $[71] = t43;
  } else {
    t43 = $[71];
  }
  let t44;
  if ($[72] !== t40 || $[73] !== t43) {
    t44 = <PageContainer title="Active Projects" description={t6}>{t40}{t43}</PageContainer>;
    $[72] = t40;
    $[73] = t43;
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  return t44;
}
function _temp(event_0) {
  return event_0.stopPropagation();
}